import React, { memo } from 'react';
import { EditorStarterItem } from '../../items/item-type';
import { BaseItem } from '../../items/shared';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { ControlsPadding } from './controls-padding';
import {
  AlignAxis,
  computeAlignOffset,
  computeUnionBounds,
  getAlignRect,
} from './align-items';
import {
  AlignHorizontalCenter,
  AlignHorizontalLeft,
  AlignHorizontalRight,
  AlignVerticalBottom,
  AlignVerticalCenter,
  AlignVerticalTop,
} from '@crabs-ai/web-icons';
import { ToggleGroup, ToggleItem } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { InspectorSubLabel } from '@/features/remotion-editor/editor/inspector/components/inspector-label';
import { useTargetItemIds } from '../utils/use-target-item-ids';

const AlignmentControlsUnmemoized: React.FC<{
  itemId: string;
}> = ({ itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);

  // 단일 선택은 캔버스 기준, 다중 선택은 아이템끼리(선택 union bbox 기준) 정렬한다.
  // audio 는 캔버스에 없는 placeholder 좌표(0,0,100,100)라 bounds 계산·이동 모두 제외한다.
  const handleAlign = React.useCallback(
    (axis: AlignAxis) => {
      setState({
        groupPropertyEdit: 'position',
        update: (state) => {
          const items = state.undoableState.items;
          const isMulti = targetIds.length > 1;
          const bounds = isMulti
            ? computeUnionBounds(
                targetIds
                  .map((id) => items[id])
                  .filter(
                    (i): i is EditorStarterItem =>
                      i != null && i.type !== 'audio',
                  )
                  .map((i) => getAlignRect(i, items)),
              )
            : {
                left: 0,
                top: 0,
                width: state.undoableState.compositionWidth,
                height: state.undoableState.compositionHeight,
              };
          // 다중인데 위치 있는 아이템이 없으면(전부 audio) 정렬할 게 없다.
          if (bounds == null) return state;

          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type === 'audio') return i;
            const rect = getAlignRect(i, items);
            const { dx, dy } = computeAlignOffset(axis, bounds, rect);
            if (dx === 0 && dy === 0) return i;

            const updatedItem: BaseItem = {
              ...(i as BaseItem),
              left: i.left + dx,
              top: i.top + dy,
            };
            return updatedItem as EditorStarterItem;
          });
        },
        commitToUndoStack: true,
      });
    },
    [setState, itemId, targetIds],
  );

  return (
    <div className="flex flex-col gap-4xs">
      <InspectorSubLabel>{t('Editor.inspector.text.align')}</InspectorSubLabel>
      <div className="flex gap-4xs">
        <ToggleGroup
          type="single"
          value={null}
        >
          <ToggleItem
            value="left"
            aria-label="Align Left"
            onClick={() => handleAlign('left')}
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.align-horizontal-left')}
          >
            <AlignHorizontalLeft />
          </ToggleItem>
          <ToggleItem
            value="center"
            aria-label="Align Center"
            onClick={() => handleAlign('h-center')}
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.align-horizontal-center')}
          >
            <AlignHorizontalCenter />
          </ToggleItem>
          <ToggleItem
            value="right"
            aria-label="Align Right"
            onClick={() => handleAlign('right')}
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.align-horizontal-right')}
          >
            <AlignHorizontalRight />
          </ToggleItem>
        </ToggleGroup>
        <ToggleGroup
          type="single"
          value={null}
        >
          <ToggleItem
            value="top"
            aria-label="Align Top"
            onClick={() => handleAlign('top')}
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.align-vertical-top')}
          >
            <AlignVerticalTop />
          </ToggleItem>
          <ToggleItem
            value="center"
            aria-label="Align Center Vertically"
            onClick={() => handleAlign('v-center')}
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.align-vertical-center')}
          >
            <AlignVerticalCenter />
          </ToggleItem>
          <ToggleItem
            value="bottom"
            aria-label="Align Bottom"
            onClick={() => handleAlign('bottom')}
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.align-vertical-bottom')}
          >
            <AlignVerticalBottom />
          </ToggleItem>
        </ToggleGroup>
        <ControlsPadding />
      </div>
    </div>
  );
};

export const AlignmentControls = memo(AlignmentControlsUnmemoized);
